import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import axios from 'axios'; // Import axios for HTTP requests

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === 'success' ? 'bg-green-500' : 'bg-red-500';

  return (
    <motion.div
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -50 }}
      className={`fixed top-4 right-4 ${bgColor} text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-2`}
    >
      <span>{message}</span>
      <button onClick={onClose} className="ml-2">
        <X size={18} />
      </button>
    </motion.div>
  );
};

const AdminCompareprice = () => {
    const navigate = useNavigate();
    const [selectedFile, setSelectedFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [comparePriceData, setComparePriceData] = useState([]);
    const [isDeleting, setIsDeleting] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

    const showToast = (message, type = 'success') => {
      setToast({ show: true, message, type });
    };

    const hideToast = () => {
      setToast({ ...toast, show: false });
    };

    const handleFileChange = (event) => {
      setSelectedFile(event.target.files[0]);
    };

    const handleBack = () => {
      navigate(-1);
    };

    const fetchComparePriceData = async () => {
        try {
            const response = await axios.get('https://citi-mobile-app.onrender.com/admin/compare-prices');
            setComparePriceData(response.data);
            showToast('Data fetched successfully');
        } catch (error) {
            console.error('Error fetching data:', error);
            showToast('Error fetching data', 'error');
        }
    };

    useEffect(() => {
        fetchComparePriceData();
    }, []);

    const handleSubmit = async (event) => {
      event.preventDefault();
      
      if (selectedFile) {
        const formData = new FormData();
        formData.append('file', selectedFile);
        
        setLoading(true);
        
        try {
          const response = await fetch('https://citi-mobile-app.onrender.com/admin/uploadcompareprice', {
            method: 'POST',
            body: formData
          });
          
          const result = await response.json();
          console.log('Server response:', result);
          showToast('File uploaded successfully');
          setLoading(false);
        } catch (error) {
          console.log('Error uploading file:', error);
          showToast('Error uploading file', 'error');
          setLoading(false);
        }
      } else {
        showToast('Please select a file first', 'error');
      }
    };

    const handleDelete = async (id) => {
        setIsDeleting(true);
        try {
            await axios.delete(`https://citi-mobile-app.onrender.com/admin/compare-prices/${id}`);
            setComparePriceData(comparePriceData.filter((item) => item._id !== id));
            showToast('Record deleted successfully');
            setIsDeleting(false);
        } catch (error) {
            console.error('Error deleting record:', error);
            showToast('Error deleting record', 'error');
            setIsDeleting(false);
        }
    };

    return (
      <div className="p-6 ml-60 max-sm:ml-0 max-sm:mt-16 max-sm:p-0 max-w-1/2 max-sm:w-full">
        <AnimatePresence>
          {toast.show && (
            <Toast 
              message={toast.message}
              type={toast.type}
              onClose={hideToast}
            />
          )}
        </AnimatePresence>

        {/* Rest of your existing JSX code remains the same */}
        <div className="flex items-center mb-4 cursor-pointer" onClick={handleBack}>
          <span className="text-gray-600 mr-2">←</span>
          <span className="text-gray-600">Import Compare Price details</span>
        </div>

        <form onSubmit={handleSubmit} className="bg-white p-6 shadow-md rounded">
          <div className="mb-6 border-dashed border-2 border-gray-300 rounded-lg h-40 flex items-center justify-center">
            {selectedFile ? (
              <p>{selectedFile.name}</p>
            ) : (
              <label htmlFor="file-upload" className="cursor-pointer">
                <div className="text-gray-500">
                  <p className="mb-2">Add file</p>
                  <input
                    id="file-upload"
                    type="file"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </div>
              </label>
            )}
          </div>

          <div className="flex justify-between items-center">
            <button
              type="button"
              onClick={handleBack}
              className="bg-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-400"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`py-2 px-4 rounded text-white ${
                loading ? 'bg-gray-400' : 'bg-blue-500 hover:bg-blue-600'
              }`}
            >
              {loading ? 'Uploading...' : 'Upload and preview'}
            </button>
          </div>
        </form>

        <div className="mt-8">
          <h2 className="text-xl font-bold mb-4">Compare Price List</h2>
          <table className="w-full border-collapse border border-gray-300">
            <thead>
              <tr>
                <th className="border border-gray-300 px-4 py-2">Vendor</th>
                <th className="border border-gray-300 px-4 py-2">Product Name</th>
                <th className="border border-gray-300 px-4 py-2">Price</th>
                <th className="border border-gray-300 px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {comparePriceData.map((item) => (
                <tr key={item._id}>
                  <td className="border border-gray-300 px-4 py-2">{item.vendor}</td>
                  <td className="border border-gray-300 px-4 py-2">{item.productName}</td>
                  <td className="border border-gray-300 px-4 py-2">₦{item.price}</td>
                  <td className="border border-gray-300 px-4 py-2">
                    <button
                      onClick={() => handleDelete(item._id)}
                      disabled={isDeleting}
                      className="bg-red-500 text-white px-4 py-1 rounded hover:bg-red-600"
                    >
                      {isDeleting ? 'Deleting...' : 'Delete'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
};

export default AdminCompareprice;